import { useQuery } from '@tanstack/react-query';
import {
  AccessorKeyColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  OverlayTrigger,
  Row,
  Table,
  Tooltip
} from 'react-bootstrap';
import { Link, Route, Routes } from 'react-router-dom';

import { getFinancialTransaction } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import { currencyFormatter } from '../../helpers/currencyFormatter';
import getStatusColor from '../../helpers/getStatusColor';
import { FinancialTransaction } from '../../types';
import { formatDateShort } from '../../utils/formatShortDate';
import { formatTime } from '../../utils/formatTime';
import AnalyticsHeader from '../../widgets/Header';
import CreateFinancialTransactionModal from './CreateFinancialTransactionModal';

interface FinancialTransactionTableProps {
  query: {
    startDate: string;
    endDate: string;
  };
}

// The ledger epoch: the transaction data was manually reset at
// 2026-09-04T22:34:00Z, so the origin is the start of the following day, 5 Sep
// (WAT). Superseded 2025-09-01T13:06:30.809Z, the previous zerorization.
//
// This range is the accumulation origin, not just a display filter: the backend
// computes cumulativeBalance / cumulativeCashBalance by summing over exactly the
// rows this range matches. It stays pinned to this literal until the ledger is
// manually reset again, at which point the date is updated by hand. Do not
// re-derive it from the current date (e.g. startOfDay(new Date())) - that walks
// the origin forward and silently drops every transaction between the reset and
// today, making the balances read low with no visible error.
const START_DATE = new Date('2026-09-04T23:00:00.000Z');
const END_DATE = new Date();

const formattedStartDate = START_DATE.toISOString();
const formattedEndDate = END_DATE.toISOString();

export const FINANCIAL_TRANSACTION_QUERY_KEY = ['financial-transaction'];

const CreateFinancialTransactionButton = ({ onClick }: { onClick: () => void }) => {
  return <Button onClick={onClick}>Create Financial Transaction</Button>;
};

const columnHelper = createColumnHelper<FinancialTransaction>();

const columns = [
  columnHelper.accessor('createdBy.fullName', {
    header: 'Created By',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    cell: info => currencyFormatter(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('action', {
    header: 'Action',
    cell: info => info.getValue(),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('type', {
    header: 'Transaction Type',
    cell: info => <Badge bg={`${getStatusColor(info.getValue())}-soft`}>{info.getValue()}</Badge>,
    footer: info => info.column.id
  }),
  columnHelper.accessor('modeOfPayment', {
    header: 'Mode of Payment',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created At',
    cell: info => formatDateShort(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created Time',
    id: 'createdTime',
    cell: info => formatTime(info.getValue()),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('cumulativeBalance', {
    header: 'Cumulative Balance',
    cell: info => currencyFormatter(Number(info.getValue())),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('cumulativeCashBalance', {
    header: 'Cumulative Cash Balance',
    cell: info => currencyFormatter(Number(info.getValue())),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('orderId', {
    header: 'Invoiced Order',
    cell: info => (info.getValue() ? <Link to={`/order/${info.getValue()}`}>View Order</Link> : null),
    footer: info => info.column.id ?? ''
  }),
  columnHelper.accessor('remarks', {
    header: 'Remarks',
    cell: info => {
      const comment = info.getValue() ?? '';
      const truncatedComment = comment?.length > 25 ? `${comment.slice(0, 25)}...` : comment;

      return (
        <OverlayTrigger placement="top" overlay={<Tooltip>{comment}</Tooltip>}>
          <span>{truncatedComment}</span>
        </OverlayTrigger>
      );
    },
    footer: info => info.column.id ?? ''
  })
];

const FinancialTransactionTable = ({ query }: FinancialTransactionTableProps) => {
  const [showCreateFinancialTransactionModal, setShowCreateFinancialTransactionModal] = useState(false);
  const compositeQueryKey = query
    ? [...FINANCIAL_TRANSACTION_QUERY_KEY, query.startDate, query.endDate]
    : FINANCIAL_TRANSACTION_QUERY_KEY;
  const { startDate, endDate } = query;
  const { data, isLoading } = useQuery({
    queryKey: compositeQueryKey,
    queryFn: () => getFinancialTransaction({ startDate, endDate })
  });
  const transactions = Array.isArray(data) ? data : [];

  const onCreateButtonClicked = () => {
    setShowCreateFinancialTransactionModal(true);
  };

  const onCreateModalClosed = () => {
    setShowCreateFinancialTransactionModal(false);
  };

  const noFinancialTransactionMessage = transactions && transactions.length === 0 && (
    <EmptyTableState message="No financial transactions yet" />
  );

  return (
    <>
      {showCreateFinancialTransactionModal && (
        <CreateFinancialTransactionModal show={showCreateFinancialTransactionModal} handleClose={onCreateModalClosed} />
      )}
      <AnalyticsHeader
        title="Financial Transactions"
        tabs={tabRoutes}
        headerButton={<CreateFinancialTransactionButton onClick={onCreateButtonClicked} />}
      />
      <div className="main-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12}>
              {isLoading ? (
                <Loader />
              ) : noFinancialTransactionMessage ? (
                noFinancialTransactionMessage
              ) : transactions && transactions.length > 0 ? (
                <TableList transactions={transactions} columns={columns} />
              ) : (
                <EmptyTableState message="No financial transactions yet." />
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

const tabRoutes = [
  {
    title: 'All',
    path: 'all',
    tabLink: '/financial-transaction/all',
    element: (
      <FinancialTransactionTable
        query={{
          startDate: formattedStartDate,
          endDate: formattedEndDate
        }}
      />
    )
  }
];

const FinancialTransactions = () => {
  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default FinancialTransactions;

interface TableListProps {
  transactions: FinancialTransaction[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: AccessorKeyColumnDef<FinancialTransaction, any>[];
}

const TableList = ({ transactions, columns }: TableListProps) => {
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);

  const tableInstance = useReactTable({
    data: transactions!,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter
  });

  return (
    <Card>
      <Card.Header>
        <InputGroup className="input-group-merge input-group-flush input-group-reverse">
          <Form.Control
            type="search"
            placeholder="Search"
            onChange={e => setGlobalFilter(e.target.value ? e.target.value : undefined)}
          />
          <InputGroup.Text>
            <FeatherIcon icon="search" size="1em" />
          </InputGroup.Text>
        </InputGroup>
      </Card.Header>

      <Table size="sm" className="card-table table-nowrap" hover responsive>
        <thead>
          {tableInstance.getHeaderGroups().map(headerGroup => {
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <th colSpan={header.colSpan} key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody>
          {tableInstance.getRowModel().rows.map(row => {
            return (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
};
